import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  pathCommandsToSvgPath,
  shapeToPathCommands,
  svgPathToPathCommands,
} from '@/lib/elements/shape-path-commands'
/**
 * Convert any shape-tool shape into a pen-path shape with the same visual geometry.
 *
 * The returned ShapeData has type='path', kindId=9, and both `path` (SVG string) and
 * `pathCommands` (backend persistence format) populated. All other properties (position,
 * size, fill, stroke, etc.) are preserved unchanged.
 *
 * If the shape is already a pen path (type='path') it is returned as-is. Otherwise, `pathCommands`
 * — not `path` — is the authoritative current geometry to reuse: it's what `shape-renderer`
 * itself draws from for any non-path shape, and what an Object Effect (Sides/Inset/Rounding) or a
 * mislabeled reload (`inferIrregularPolygonShapeType` — see `checkpoint-to-shapes.ts` — guesses
 * `shape.type` when `editorShape` metadata doesn't survive the protobuf/S3 round-trip) writes —
 * `shape.path` is never touched by any of those, so it can be stale (a loaded shape that later had
 * an effect re-applied this session) or never populated at all (a shape created client-side this
 * session). `NodeEditOverlay` only reads `shape.path` to seed its anchors, though, so `path` is
 * ALWAYS regenerated from `pathCommands` here — never trusted from the shape as-is — whenever
 * `pathCommands` is present. Without this, editing a shape right after applying an effect to it
 * would silently discard the effect and regenerate a generic preset outline from `shape.type`
 * instead (WMPCM "Sides effect is lost when entering Edit Path mode").
 */
export function convertShapeToPenPath(shape: ShapeData): ShapeData {
  if (shape.type === 'path') return shape

  if (Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0) {
    const path = pathCommandsToSvgPath(shape.pathCommands, { close: true })

    return {
      ...shape,
      type: 'path',
      path,
      pathCommands: shape.pathCommands,
      kindId: 9,
      photoFill: shape.photoFill,
    }
  }

  if (shape.path) {
    return {
      ...shape,
      type: 'path',
      path: shape.path,
      pathCommands: svgPathToPathCommands(shape.path),
      kindId: 9,
      photoFill: shape.photoFill,
    }
  }

  const commands = shapeToPathCommands(shape.type, shape.width, shape.height)
  const path = pathCommandsToSvgPath(commands, { close: true })
  const pathCommands = svgPathToPathCommands(path)

  return {
    ...shape,
    type: 'path',
    path,
    pathCommands,
    kindId: 9,
    photoFill: shape.photoFill,
  }
}
